import type {Dayjs} from 'dayjs';

/**
 * 舆情 POD 审核 —— 前端类型定义
 *
 * 与 api-server `api/graphql/podRiskReview/types/PodRiskReviewTypes.kt` 一一对应。
 * 后端 Long 型 ID 一律以 String 传输（避免 JS 精度丢失），时间为 epoch seconds。
 */

/** 审核结论；后端为通用引擎态，展示文案在前端转换（APPROVED→不存在风险，REJECTED→存在舆情风险） */
export enum PodRiskReviewStatus {
    PENDING_REVIEW = 'PENDING_REVIEW',
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    CANCELLED = 'CANCELLED'
}

/** 工作台 Tab（仅前端 UI 状态；查询时转为 isFinished 传给后端） */
export enum PodRiskReviewTabType {
    /** 待审核 */
    NEW = 'NEW',
    /** 已终止：审核已结束（不存在风险 / 存在舆情风险 / 已取消） */
    TERMINATED = 'TERMINATED'
}

/** Tab → 查询条件；后端按 pod_risk_review.is_finished 过滤 */
export const tabToIsFinished = (tab?: PodRiskReviewTabType): boolean | undefined =>
    tab === undefined ? undefined : tab === PodRiskReviewTabType.TERMINATED;

/** 审核动作：APPROVE-不存在风险，REJECT-存在舆情风险（与 POP 的合格/不合格语义相反） */
export enum PodRiskReviewAction {
    APPROVE = 'APPROVE',
    REJECT = 'REJECT'
}

/** 该状态是否为终态 */
export const isPodRiskReviewFinished = (status?: PodRiskReviewStatus): boolean =>
    !!status && status !== PodRiskReviewStatus.PENDING_REVIEW;

/**
 * 一期是否开放审核入口。一期只做查询，审核能力二期开放：
 * 关闭时隐藏批量审核按钮、Drawer 的提交按钮与已审单的结论按钮，
 * 详情仍可查看（含 POD 图、风险类型、审核备注与操作日志）。
 * 后端 submitPodRiskReviewAction 接口保持可用，故开放时只需改本常量。
 */
export const POD_RISK_REVIEW_ACTION_ENABLED = false;

/**
 * 是否开放下载入口。关闭时隐藏列表页的下载按钮。
 * 后端 downloadPodRiskReview 接口保持可用，开放时只需改本常量。
 */
export const POD_RISK_REVIEW_DOWNLOAD_ENABLED = false;

/**
 * 时间区间（前端筛选态）：持有 dayjs 对象，转查询入参时才规整为 ISO 字符串。
 * 后端为 `>= from` / `< to`（不含），详见 usePodRiskReviewQuery。
 */
export type TimeRange = [Dayjs, Dayjs];

/** 多语言文案项 */
export interface LangTextEntry {
    /** BCP-47 short code：zh|en|es */
    lang: string;
    text: string;
}

/**
 * 风险类型字典项；文案单一来源为服务端枚举，前端按 locale 自取，不拼 i18n key。
 * 服务端已按 displayOrder 排好序，顺序渲染即可。
 */
export interface PodRiskTypeOption {
    code?: string;
    displayOrder: number;
    texts: LangTextEntry[];
}

/** 审核备注单条译文 */
export interface PodRiskReviewRemarkTranslation {
    /** BCP-47 short code：zh|en|es */
    lang: string;
    text: string;
}

/**
 * 审核备注的原文与译文。
 *
 * 译文由后端异步补且允许丢失，取不到时必须回落 text。
 */
export interface PodRiskReviewRemark {
    /** 原文；译文无命中时的兜底 */
    text: string;
    /** 原文语种 short code */
    lang: string;
    /** 译文列表，不含源语种；可为空数组 */
    translations: PodRiskReviewRemarkTranslation[];
}

/** 操作日志（与 POP 审核共用后端 PopReviewLogType，字段须与其保持一致） */
export interface PodRiskReviewLog {
    id?: string;
    action?: string;
    /** 操作人 account ID（0=系统）；后端不回查姓名 */
    operatorId?: string;
    /** epoch seconds */
    createdAt?: number;
    /** 本次操作的备注 */
    comment?: string;
}

/**
 * 工作台列表行
 *
 * 名称类字段两级兜底：后端 DataLoader 回查的名称 → 回落显示 ID。
 * 配送商名称后端不回查，由前端用 DSP 下拉的 id→name 映射匹配。
 */
export interface PodRiskReviewType {
    reviewTaskId: string;
    status: PodRiskReviewStatus;
    /** 审核完成截止时间（epoch seconds；SLA 超时仅告警，不改状态） */
    slaDueAt?: number;
    reviewerId?: string;
    /** 审核人信息（后端 DataLoader 回查 account）；未审核时为 null */
    reviewer?: {name?: string};
    /** 审核完成时间（epoch seconds） */
    reviewedAt?: number;
    /** 是否已完结 */
    finished: boolean;
    /** 完结时间（epoch seconds） */
    finishedAt?: number;
    /** 创建时间（epoch seconds）；列表排序与「创建时间」展示列同源 */
    createdAt?: number;

    podRiskReviewId?: string;
    orderId?: string;
    trackingNumber?: string;
    /** 配送商 ID；名称用 DSP 下拉的 id→name 映射 */
    dspId?: string;
    /** 妥投司机 account ID */
    driverId?: string;
    /** 妥投司机姓名（后端 DataLoader 回查 account）；查不到为 null，前端回落显示 ID */
    driverName?: string;
    warehouseId?: string;
    /** 仓库名称（后端 DataLoader 回查）；查不到为 null，前端回落显示 ID */
    warehouseName?: string;
    /** POD 图片送检批次号；图片 URL 仅详情返回 */
    batchNo?: string;
    /** 风控请求 ID（排障配对用） */
    requestId?: string;
    /** 风控原始命中类型；仅供排障，不用于展示分类 */
    machineHitTypes: string[];
    /** 风控命中图片张数（建单时点） */
    machineHitImageCount?: number;
    /** 风控模型版本（建单时点） */
    /** 妥投时间（epoch seconds） */
    deliveredAt?: number;

    /** 审核员确认的风险类型码；待审核单与「不存在风险」的单为空数组 */
    riskTypeCodes: string[];
    /** 审核备注（原文 + 多语译文）；无备注为 null */
    reviewRemark?: PodRiskReviewRemark;
}

/** POD 图片，isRisky 标记该图是否被机审命中 */
export interface PodRiskReviewImage {
    /** 预签名 URL（有效期 10 分钟）；生成失败的已被后端过滤 */
    url: string;
    isRisky: boolean;
}

/** 详情：列表行 + POD 图片 + 操作日志 */
export interface PodRiskReviewDetail {
    item: PodRiskReviewType;
    podImages: PodRiskReviewImage[];
    /** 操作日志（按时间升序） */
    logs: PodRiskReviewLog[];
}

/** 审核结果 */
export interface PodRiskReviewActionResult {
    reviewTaskId?: string;
    orderId?: string;
    trackingNumber?: string;
    status: PodRiskReviewStatus;
    reviewerId?: string;
    reviewedAt?: number;
}

/**
 * 筛选条件（前端态）
 *
 * 时间区间以 dayjs 元组持有，提交时拆成 xxxFrom / xxxTo 的 ISO-8601 字符串。
 */
export interface PodRiskReviewFilters {
    /**
     * Tab 由页面单独持有，不放入本对象。
     * 后端 filters 支持 reviewTaskId 精确匹配，但筛选区不暴露 —— 该 ID 对操作人员不可见。
     */
    statuses?: PodRiskReviewStatus[];
    /** 运单号，支持多值（分号 / 逗号 / 换行 分隔） */
    trackingNumbers?: string;
    /** 渠道订单号，支持多值（分号 / 逗号 / 换行 分隔） */
    channelOrderNumbers?: string;
    dspIds?: string[];
    driverIds?: string[];
    warehouseIds?: string[];
    /**
     * 中心（Hub）ID 多选：仅用于筛选区级联收敛仓库/区域候选，不提交后端。
     * 后端按三段码筛选，见 hubSortingCodes。
     */
    hubIds?: string[];
    /** 中心（Hub）三段码多选，后端按前缀匹配 */
    hubSortingCodes?: string[];
    /** 区域三段码多选，后端精确匹配 */
    areaSortingCodes?: string[];
    /** 审核人 account ID */
    reviewerId?: string;
    /** 风险类型码多选；命中任一即返回 */
    riskTypeCodes?: string[];
    deliveredAt?: TimeRange;
    createdAt?: TimeRange;
    reviewedAt?: TimeRange;
}
